import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  FileText,
  MapPin,
  Users,
  Settings,
  Ship,
  Plane,
  Truck,
  Train,
  Container,
  Anchor,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  rfqApi,
  carrierApi,
  rateTemplatesApi,
  RFQType,
  ShipmentType,
  TransportMode,
  ContainerType,
  Incoterm,
  Carrier,
  RateTemplate,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

// Types
interface RFQFormData {
  title: string;
  description: string;
  type: RFQType;
  shipmentType: ShipmentType;
  incoterm?: Incoterm;
  containerTypes: ContainerType[];
  responseDeadline: string;
  awardTargetDate: string;
  rateEffectiveDate: string;
  rateExpirationDate: string;
  cargoDescription: string;
  hazmatRequired: boolean;
  hazmatClass?: string;
  unNumber?: string;
  temperatureControlled: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit: string;
  estimatedAnnualVolume?: number;
  volumeCommitmentType: string;
  baseCurrency: string;
  maxBidSubmissions: number;
  allowBidRevision: boolean;
  workScope: string;
  specialInstructions: string;
  rateTemplateId?: string;
}

interface LaneData {
  id: string;
  lineNumber: number;
  originCity: string;
  originState: string;
  originCountry: string;
  originPort?: string;
  destCity: string;
  destState: string;
  destCountry: string;
  destPort?: string;
  mode: TransportMode;
  containerType?: ContainerType;
  containerCount?: number;
  estimatedShipments?: number;
  estimatedWeight?: number;
  currentRate?: number;
  targetRate?: number;
}

const SHIPMENT_TYPE_OPTIONS: { value: ShipmentType; label: string; icon: React.ReactNode }[] = [
  { value: 'OCEAN_FCL', label: 'Ocean FCL', icon: <Ship className="w-5 h-5" /> },
  { value: 'OCEAN_LCL', label: 'Ocean LCL', icon: <Container className="w-5 h-5" /> },
  { value: 'AIR', label: 'Air Freight', icon: <Plane className="w-5 h-5" /> },
  { value: 'FTL', label: 'Full Truckload', icon: <Truck className="w-5 h-5" /> },
  { value: 'LTL', label: 'Less Than Truckload', icon: <Truck className="w-5 h-5" /> },
  { value: 'RAIL', label: 'Rail', icon: <Train className="w-5 h-5" /> },
  { value: 'BREAK_BULK', label: 'Break Bulk', icon: <Anchor className="w-5 h-5" /> },
  { value: 'INTERMODAL', label: 'Intermodal', icon: <Container className="w-5 h-5" /> },
];

const CONTAINER_TYPES: { value: ContainerType; label: string }[] = [
  { value: 'GP20', label: "20' General Purpose" },
  { value: 'GP40', label: "40' General Purpose" },
  { value: 'HQ40', label: "40' High Cube" },
  { value: 'RF20', label: "20' Reefer" },
  { value: 'RF40', label: "40' Reefer" },
  { value: 'OT20', label: "20' Open Top" },
  { value: 'OT40', label: "40' Open Top" },
  { value: 'FR20', label: "20' Flat Rack" },
  { value: 'FR40', label: "40' Flat Rack" },
];

const INCOTERMS: { value: Incoterm; label: string; description: string }[] = [
  { value: 'EXW', label: 'Ex Works', description: 'Buyer bears all costs and risks' },
  { value: 'FCA', label: 'Free Carrier', description: 'Seller delivers to carrier' },
  { value: 'FOB', label: 'Free on Board', description: 'Seller delivers on vessel' },
  { value: 'CIF', label: 'Cost, Insurance & Freight', description: 'Seller pays to destination port' },
  { value: 'CFR', label: 'Cost & Freight', description: 'Seller pays freight to destination' },
  { value: 'DAP', label: 'Delivered at Place', description: 'Seller delivers to named place' },
  { value: 'DDP', label: 'Delivered Duty Paid', description: 'Seller bears all costs' },
];

const STEPS = [
  { id: 'basic', label: 'Basic Info', icon: <FileText className="w-4 h-4" /> },
  { id: 'cargo', label: 'Cargo Details', icon: <Container className="w-4 h-4" /> },
  { id: 'lanes', label: 'Lanes', icon: <MapPin className="w-4 h-4" /> },
  { id: 'carriers', label: 'Carriers', icon: <Users className="w-4 h-4" /> },
  { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
];

const getDefaultMode = (shipmentType: ShipmentType): TransportMode => {
  switch (shipmentType) {
    case 'OCEAN_FCL':
    case 'OCEAN_LCL':
    case 'BREAK_BULK':
      return 'Ocean';
    case 'AIR':
      return 'Air';
    case 'FTL':
      return 'TruckFTL';
    case 'LTL':
      return 'TruckLTL';
    case 'RAIL':
      return 'Rail';
    case 'INTERMODAL':
      return 'Intermodal';
    default:
      return 'TruckFTL';
  }
};

export default function FreightRFQCreate() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<RFQFormData>({
    title: '',
    description: '',
    type: 'Annual',
    shipmentType: 'OCEAN_FCL',
    containerTypes: [],
    responseDeadline: '',
    awardTargetDate: '',
    rateEffectiveDate: '',
    rateExpirationDate: '',
    cargoDescription: '',
    hazmatRequired: false,
    temperatureControlled: false,
    temperatureUnit: 'C',
    volumeCommitmentType: 'Estimated',
    baseCurrency: 'USD',
    maxBidSubmissions: 3,
    allowBidRevision: true,
    workScope: '',
    specialInstructions: '',
  });

  const [lanes, setLanes] = useState<LaneData[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [availableCarriers, setAvailableCarriers] = useState<Carrier[]>([]);
  const [rateTemplates, setRateTemplates] = useState<RateTemplate[]>([]);

  const updateFormData = (field: keyof RFQFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addLane = () => {
    const newLane: LaneData = {
      id: `temp-${Date.now()}`,
      lineNumber: lanes.length + 1,
      originCity: '',
      originState: '',
      originCountry: 'USA',
      destCity: '',
      destState: '',
      destCountry: 'USA',
      mode: getDefaultMode(formData.shipmentType),
    };
    setLanes([...lanes, newLane]);
  };

  const updateLane = (index: number, field: keyof LaneData, value: unknown) => {
    const updated = [...lanes];
    updated[index] = { ...updated[index], [field]: value };
    setLanes(updated);
  };

  const removeLane = (index: number) => {
    const updated = lanes.filter((_, i) => i !== index);
    updated.forEach((lane, i) => {
      lane.lineNumber = i + 1;
    });
    setLanes(updated);
  };

  const loadCarriers = async () => {
    try {
      const response = await carrierApi.getAll();
      setAvailableCarriers(response.data);
    } catch (error) {
      console.error('Failed to load carriers:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const loadRateTemplates = async () => {
    try {
      const response = await rateTemplatesApi.getAll({
        shipmentType: formData.shipmentType,
        isActive: true,
      });
      setRateTemplates(response.data);
      // Auto-select default template if one exists
      const defaultTemplate = response.data.find((t: RateTemplate) => t.isDefault);
      if (defaultTemplate && !formData.rateTemplateId) {
        updateFormData('rateTemplateId', defaultTemplate.id);
      }
    } catch (error) {
      console.error('Failed to load rate templates:', error);
    }
  };

  const handleNext = async () => {
    if (currentStep === 3 && availableCarriers.length === 0) {
      await loadCarriers();
    }
    // Load rate templates when moving to Settings step
    if (currentStep === 3 && rateTemplates.length === 0) {
      await loadRateTemplates();
    }
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSave = async (asDraft = true) => {
    try {
      setSaving(true);

      // Create the RFQ
      const response = await rfqApi.create({
        title: formData.title,
        description: formData.description,
        type: formData.type,
        shipmentType: formData.shipmentType,
        incoterm: formData.incoterm,
        containerTypes: formData.containerTypes,
        responseDeadline: formData.responseDeadline,
        awardTargetDate: formData.awardTargetDate || undefined,
        rateEffectiveDate: formData.rateEffectiveDate,
        rateExpirationDate: formData.rateExpirationDate,
        cargoDescription: formData.cargoDescription || undefined,
        hazmatRequired: formData.hazmatRequired,
        temperatureControlled: formData.temperatureControlled,
        temperatureMin: formData.temperatureMin,
        temperatureMax: formData.temperatureMax,
        estimatedAnnualVolume: formData.estimatedAnnualVolume,
        baseCurrency: formData.baseCurrency,
        maxBidSubmissions: formData.maxBidSubmissions,
        allowBidRevision: formData.allowBidRevision,
        workScope: formData.workScope || undefined,
        specialInstructions: formData.specialInstructions || undefined,
        rateTemplateId: formData.rateTemplateId || undefined,
      });

      const rfq = response;

      // Add lanes
      for (const lane of lanes) {
        await rfqApi.addLine(rfq.id, {
          lineNumber: lane.lineNumber,
          originCity: lane.originCity,
          originState: lane.originState,
          originCountry: lane.originCountry,
          originPort: lane.originPort,
          destCity: lane.destCity,
          destState: lane.destState,
          destCountry: lane.destCountry,
          destPort: lane.destPort,
          mode: lane.mode,
          containerType: lane.containerType,
          containerCount: lane.containerCount,
          estimatedShipments: lane.estimatedShipments,
          estimatedWeight: lane.estimatedWeight,
          currentRate: lane.currentRate,
          targetRate: lane.targetRate,
        });
      }

      // Invite carriers
      if (selectedCarriers.length > 0) {
        await rfqApi.inviteCarriers(rfq.id, { carrierIds: selectedCarriers });
      }

      // Open RFQ if not saving as draft
      if (!asDraft) {
        await rfqApi.send(rfq.id);
      }

      toast.success(asDraft ? 'RFQ saved as draft' : 'RFQ created and opened');
      navigate(`/tms/rfq/${rfq.id}`);
    } catch (error) {
      console.error('Failed to create RFQ:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep formData={formData} updateFormData={updateFormData} />;
      case 1:
        return <CargoDetailsStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <LanesStep
            lanes={lanes}
            shipmentType={formData.shipmentType}
            addLane={addLane}
            updateLane={updateLane}
            removeLane={removeLane}
          />
        );
      case 3:
        return (
          <CarriersStep
            availableCarriers={availableCarriers}
            selectedCarriers={selectedCarriers}
            setSelectedCarriers={setSelectedCarriers}
          />
        );
      case 4:
        return <SettingsStep formData={formData} updateFormData={updateFormData} rateTemplates={rateTemplates} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Create Freight RFQ"
        description="Request quotes from carriers"
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/rfq')}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        }
      />

      {/* Progress Steps */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => setCurrentStep(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  index === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : index < currentStep
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : step.icon}
                <span className="font-medium">{step.label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <ChevronRight className="w-5 h-5 text-muted-foreground mx-2" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-card rounded-xl border p-6">{renderStepContent()}</div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}>
            Save as Draft
          </Button>
          {currentStep === STEPS.length - 1 ? (
            <Button onClick={() => handleSave(false)} disabled={saving}>
              {saving ? 'Creating...' : 'Create & Open RFQ'}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step Components
function BasicInfoStep({
  formData,
  updateFormData,
}: {
  formData: RFQFormData;
  updateFormData: (field: keyof RFQFormData, value: unknown) => void;
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Basic Information</h2>

      {/* Shipment Type Selection */}
      <div>
        <label className="block text-sm font-medium mb-3">Shipment Type *</label>
        <div className="grid grid-cols-4 gap-3">
          {SHIPMENT_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => updateFormData('shipmentType', option.value)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                formData.shipmentType === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/30'
              }`}
            >
              <div
                className={`${
                  formData.shipmentType === option.value
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                {option.icon}
              </div>
              <span
                className={`text-sm font-medium ${
                  formData.shipmentType === option.value
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-1">RFQ Title *</label>
          <Input
            value={formData.title}
            onChange={(e) => updateFormData('title', e.target.value)}
            placeholder="e.g., Q1 2025 Ocean FCL - Asia to US West Coast"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">RFQ Type *</label>
          <Select
            value={formData.type}
            onValueChange={(value) => updateFormData('type', value as RFQType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Annual">Annual Contract</SelectItem>
              <SelectItem value="NewLane">New Lane</SelectItem>
              <SelectItem value="Spot">Spot Quote</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value)}
          rows={3}
          placeholder="Provide additional details about this RFQ..."
        />
      </div>

      {/* Incoterm for Ocean/Air */}
      {['OCEAN_FCL', 'OCEAN_LCL', 'AIR', 'BREAK_BULK'].includes(formData.shipmentType) && (
        <div>
          <label className="block text-sm font-medium mb-1">Incoterm</label>
          <div className="grid grid-cols-4 gap-2">
            {INCOTERMS.map((term) => (
              <button
                key={term.value}
                onClick={() => updateFormData('incoterm', term.value)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.incoterm === term.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="font-semibold text-sm">{term.value}</div>
                <div className="text-xs text-muted-foreground">{term.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Container Types for Ocean */}
      {['OCEAN_FCL', 'OCEAN_LCL'].includes(formData.shipmentType) && (
        <div>
          <label className="block text-sm font-medium mb-1">Accepted Container Types</label>
          <div className="flex flex-wrap gap-2">
            {CONTAINER_TYPES.map((ct) => (
              <button
                key={ct.value}
                onClick={() => {
                  const types = formData.containerTypes.includes(ct.value)
                    ? formData.containerTypes.filter((t) => t !== ct.value)
                    : [...formData.containerTypes, ct.value];
                  updateFormData('containerTypes', types);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  formData.containerTypes.includes(ct.value)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dates */}
      <div>
        <label className="block text-sm font-medium mb-2">Key Dates</label>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Response Deadline *
            </label>
            <Input
              type="date"
              value={formData.responseDeadline}
              onChange={(e) => updateFormData('responseDeadline', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Award Target Date
            </label>
            <Input
              type="date"
              value={formData.awardTargetDate}
              onChange={(e) => updateFormData('awardTargetDate', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Rate Effective Date *
            </label>
            <Input
              type="date"
              value={formData.rateEffectiveDate}
              onChange={(e) => updateFormData('rateEffectiveDate', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Rate Expiration Date *
            </label>
            <Input
              type="date"
              value={formData.rateExpirationDate}
              onChange={(e) => updateFormData('rateExpirationDate', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CargoDetailsStep({
  formData,
  updateFormData,
}: {
  formData: RFQFormData;
  updateFormData: (field: keyof RFQFormData, value: unknown) => void;
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Cargo Details</h2>

      <div>
        <label className="block text-sm font-medium mb-1">Cargo Description</label>
        <Textarea
          value={formData.cargoDescription}
          onChange={(e) => updateFormData('cargoDescription', e.target.value)}
          rows={3}
          placeholder="Describe the cargo being shipped..."
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-1">Estimated Annual Volume</label>
          <Input
            type="number"
            value={formData.estimatedAnnualVolume || ''}
            onChange={(e) =>
              updateFormData('estimatedAnnualVolume', parseInt(e.target.value) || undefined)
            }
            placeholder="e.g., 500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Volume Commitment Type</label>
          <Select
            value={formData.volumeCommitmentType}
            onValueChange={(value) => updateFormData('volumeCommitmentType', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Estimated">Estimated</SelectItem>
              <SelectItem value="Committed">Committed</SelectItem>
              <SelectItem value="MinMax">Min/Max Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Hazmat */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <Checkbox
            id="hazmat"
            checked={formData.hazmatRequired}
            onCheckedChange={(checked) => updateFormData('hazmatRequired', checked)}
          />
          <label htmlFor="hazmat" className="font-medium cursor-pointer">
            Hazardous Materials
          </label>
        </div>
        {formData.hazmatRequired && (
          <div className="grid grid-cols-2 gap-4 ml-7">
            <div>
              <label className="block text-sm font-medium mb-1">Hazmat Class</label>
              <Input
                value={formData.hazmatClass || ''}
                onChange={(e) => updateFormData('hazmatClass', e.target.value)}
                placeholder="e.g., Class 3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">UN Number</label>
              <Input
                value={formData.unNumber || ''}
                onChange={(e) => updateFormData('unNumber', e.target.value)}
                placeholder="e.g., UN1203"
              />
            </div>
          </div>
        )}
      </div>

      {/* Temperature Controlled */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <Checkbox
            id="temp"
            checked={formData.temperatureControlled}
            onCheckedChange={(checked) => updateFormData('temperatureControlled', checked)}
          />
          <label htmlFor="temp" className="font-medium cursor-pointer">
            Temperature Controlled
          </label>
        </div>
        {formData.temperatureControlled && (
          <div className="grid grid-cols-3 gap-4 ml-7">
            <div>
              <label className="block text-sm font-medium mb-1">Min Temperature</label>
              <Input
                type="number"
                value={formData.temperatureMin || ''}
                onChange={(e) =>
                  updateFormData('temperatureMin', parseInt(e.target.value) || undefined)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Temperature</label>
              <Input
                type="number"
                value={formData.temperatureMax || ''}
                onChange={(e) =>
                  updateFormData('temperatureMax', parseInt(e.target.value) || undefined)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Unit</label>
              <Select
                value={formData.temperatureUnit}
                onValueChange={(value) => updateFormData('temperatureUnit', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="C">Celsius</SelectItem>
                  <SelectItem value="F">Fahrenheit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LanesStep({
  lanes,
  shipmentType,
  addLane,
  updateLane,
  removeLane,
}: {
  lanes: LaneData[];
  shipmentType: ShipmentType;
  addLane: () => void;
  updateLane: (index: number, field: keyof LaneData, value: unknown) => void;
  removeLane: (index: number) => void;
}) {
  const showPorts = ['OCEAN_FCL', 'OCEAN_LCL', 'BREAK_BULK'].includes(shipmentType);
  const showContainers = ['OCEAN_FCL', 'OCEAN_LCL'].includes(shipmentType);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Lanes / Routes</h2>
        <Button onClick={addLane}>
          <Plus className="w-4 h-4 mr-2" />
          Add Lane
        </Button>
      </div>

      {lanes.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-xl">
          <MapPin className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No lanes added yet</p>
          <Button variant="outline" onClick={addLane}>
            <Plus className="w-4 h-4 mr-2" />
            Add your first lane
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {lanes.map((lane, index) => (
            <div key={lane.id} className="border rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Lane #{lane.lineNumber}</span>
                <button
                  onClick={() => removeLane(index)}
                  className="text-destructive hover:text-destructive/80"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Origin */}
                <div className="space-y-3">
                  <h4 className="font-medium text-muted-foreground">Origin</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={lane.originCity}
                      onChange={(e) => updateLane(index, 'originCity', e.target.value)}
                      placeholder="City"
                    />
                    <Input
                      value={lane.originState}
                      onChange={(e) => updateLane(index, 'originState', e.target.value)}
                      placeholder="State"
                    />
                  </div>
                  {showPorts && (
                    <Input
                      value={lane.originPort || ''}
                      onChange={(e) => updateLane(index, 'originPort', e.target.value)}
                      placeholder="Port (e.g., CNSHA)"
                    />
                  )}
                </div>

                {/* Destination */}
                <div className="space-y-3">
                  <h4 className="font-medium text-muted-foreground">Destination</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={lane.destCity}
                      onChange={(e) => updateLane(index, 'destCity', e.target.value)}
                      placeholder="City"
                    />
                    <Input
                      value={lane.destState}
                      onChange={(e) => updateLane(index, 'destState', e.target.value)}
                      placeholder="State"
                    />
                  </div>
                  {showPorts && (
                    <Input
                      value={lane.destPort || ''}
                      onChange={(e) => updateLane(index, 'destPort', e.target.value)}
                      placeholder="Port (e.g., USLAX)"
                    />
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-4 gap-3">
                {showContainers && (
                  <>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">
                        Container Type
                      </label>
                      <Select
                        value={lane.containerType || ''}
                        onValueChange={(value) =>
                          updateLane(index, 'containerType', value as ContainerType)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTAINER_TYPES.map((ct) => (
                            <SelectItem key={ct.value} value={ct.value}>
                              {ct.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">
                        Container Count
                      </label>
                      <Input
                        type="number"
                        value={lane.containerCount || ''}
                        onChange={(e) =>
                          updateLane(index, 'containerCount', parseInt(e.target.value) || undefined)
                        }
                        placeholder="Qty"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    Est. Shipments/Year
                  </label>
                  <Input
                    type="number"
                    value={lane.estimatedShipments || ''}
                    onChange={(e) =>
                      updateLane(index, 'estimatedShipments', parseInt(e.target.value) || undefined)
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Target Rate</label>
                  <Input
                    type="number"
                    value={lane.targetRate || ''}
                    onChange={(e) =>
                      updateLane(index, 'targetRate', parseFloat(e.target.value) || undefined)
                    }
                    placeholder="$"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarriersStep({
  availableCarriers,
  selectedCarriers,
  setSelectedCarriers,
}: {
  availableCarriers: Carrier[];
  selectedCarriers: string[];
  setSelectedCarriers: (carriers: string[]) => void;
}) {
  const toggleCarrier = (carrierId: string) => {
    if (selectedCarriers.includes(carrierId)) {
      setSelectedCarriers(selectedCarriers.filter((id) => id !== carrierId));
    } else {
      setSelectedCarriers([...selectedCarriers, carrierId]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Invite Carriers</h2>
        <span className="text-sm text-muted-foreground">
          {selectedCarriers.length} carrier(s) selected
        </span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {availableCarriers.map((carrier) => (
          <div
            key={carrier.id}
            onClick={() => toggleCarrier(carrier.id)}
            className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedCarriers.includes(carrier.id)
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/30'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedCarriers.includes(carrier.id)
                    ? 'bg-primary border-primary'
                    : 'border-muted-foreground/30'
                }`}
              >
                {selectedCarriers.includes(carrier.id) && (
                  <Check className="w-3 h-3 text-primary-foreground" />
                )}
              </div>
              <div>
                <div className="font-medium">{carrier.name}</div>
                <div className="text-sm text-muted-foreground">
                  {carrier.code} - {carrier.type}
                </div>
              </div>
            </div>
            <div className="text-right">
              {carrier.onTimeDeliveryRate && (
                <div className="text-sm">{carrier.onTimeDeliveryRate}% On-Time</div>
              )}
              <div className="text-xs text-muted-foreground">
                {carrier.serviceTypes?.join(', ')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {availableCarriers.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed rounded-xl">
          <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading carriers...</p>
        </div>
      )}
    </div>
  );
}

function SettingsStep({
  formData,
  updateFormData,
  rateTemplates,
}: {
  formData: RFQFormData;
  updateFormData: (field: keyof RFQFormData, value: unknown) => void;
  rateTemplates: RateTemplate[];
}) {
  const selectedTemplate = rateTemplates.find((t) => t.id === formData.rateTemplateId);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">RFQ Settings</h2>

      {/* Rate Template Selection */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium">Rate Template</label>
        <Select
          value={formData.rateTemplateId || ''}
          onValueChange={(value) => updateFormData('rateTemplateId', value || undefined)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a rate template (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">No template</SelectItem>
            {rateTemplates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.name}
                {template.isDefault && ' (Default)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedTemplate && (
          <div className="text-sm text-muted-foreground">
            <p>{selectedTemplate.description || 'No description'}</p>
            <p className="mt-1">
              Includes {selectedTemplate.chargeStructure?.length || 0} charge types
              {selectedTemplate.chargeStructure && selectedTemplate.chargeStructure.length > 0 && (
                <span className="ml-1">
                  ({selectedTemplate.chargeStructure.slice(0, 3).map((c) => c.chargeName).join(', ')}
                  {selectedTemplate.chargeStructure.length > 3 && `, +${selectedTemplate.chargeStructure.length - 3} more`})
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium mb-1">Base Currency</label>
          <Select
            value={formData.baseCurrency}
            onValueChange={(value) => updateFormData('baseCurrency', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD - US Dollar</SelectItem>
              <SelectItem value="EUR">EUR - Euro</SelectItem>
              <SelectItem value="GBP">GBP - British Pound</SelectItem>
              <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Max Bid Submissions</label>
          <Input
            type="number"
            min={1}
            max={10}
            value={formData.maxBidSubmissions}
            onChange={(e) =>
              updateFormData('maxBidSubmissions', parseInt(e.target.value) || 3)
            }
          />
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <Checkbox
              id="allowBidRevision"
              checked={formData.allowBidRevision}
              onCheckedChange={(checked) => updateFormData('allowBidRevision', checked)}
            />
            <label htmlFor="allowBidRevision" className="text-sm font-medium cursor-pointer">
              Allow Bid Revision
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Work Scope</label>
        <Textarea
          value={formData.workScope}
          onChange={(e) => updateFormData('workScope', e.target.value)}
          rows={3}
          placeholder="Describe the scope of work expected from carriers..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Special Instructions</label>
        <Textarea
          value={formData.specialInstructions}
          onChange={(e) => updateFormData('specialInstructions', e.target.value)}
          rows={3}
          placeholder="Any special instructions or requirements for carriers..."
        />
      </div>
    </div>
  );
}
